import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { PageShell } from "@/components/mai/PageShell";
import { useProduct } from "@/context/ProductContext";
import { CircleNotch, PlayCircle, CheckCircle, WarningCircle, ShieldCheck, ShieldWarning, DownloadSimple } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Chip({ tone, children }) {
  return <span className={`inline-block text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 border ${tone}`}>{children}</span>;
}

const SEV_TONE = {
  critical: "bg-red-100 text-red-800 border-red-200",
  high: "bg-amber-50 text-amber-700 border-amber-300",
  medium: "bg-blue-50 text-blue-700 border-blue-200",
  low: "bg-slate-100 text-slate-600 border-slate-200",
};

export default function ValidationPage({ embedded }) {
  const { productId, product } = useProduct();
  const [report, setReport] = useState(null);
  const [running, setRunning] = useState(false);
  const [showAllPassed, setShowAllPassed] = useState(false);

  const loadLatest = useCallback(() => {
    if (!productId) return;
    axios.get(`${API}/validation/${productId}/latest`)
      .then((r) => setReport(r.data.generated_at ? r.data : null))
      .catch(() => {});
  }, [productId]);
  useEffect(() => { loadLatest(); }, [loadLatest]);

  const run = async () => {
    setRunning(true);
    try {
      const r = await axios.post(`${API}/validation/${productId}/run`);
      setReport(r.data);
      toast.success(`Validation complete: ${r.data.totals.passed}/${r.data.totals.checks} passed`);
    } catch (e) { toast.error("Validation run failed"); }
    finally { setRunning(false); }
  };

  const exportJson = () => {
    if (!report) return;
    const blob = new Blob([JSON.stringify(report, null, 2)],
      { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `validation-${productId}-${report.generated_at?.slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <PageShell embedded={embedded} movedTo={{ label: "Evidence Review", to: "/console/evidence-review?tab=validation" }} pageId="validation" overline={`Workspace Validation · Prompt 16 · ${product?.name || ""}`}
      title="Full Workspace Validation">
      <div className="flex flex-wrap items-center gap-2">
        <button data-testid="run-validation" onClick={run} disabled={running}
          className="text-[11px] font-semibold text-white bg-slate-900 hover:bg-slate-800 px-3 py-2 disabled:opacity-50 flex items-center gap-1.5">
          {running ? <CircleNotch size={11} className="animate-spin" /> : <PlayCircle size={11} weight="fill" />}
          Run full validation
        </button>
        {report && (
          <button data-testid="validation-export" onClick={exportJson}
            className="text-[11px] font-semibold text-slate-700 border border-slate-300 px-3 py-2 hover:bg-slate-50 flex items-center gap-1.5">
            <DownloadSimple size={11} /> Export JSON
          </button>
        )}
        {report && (
          <p className="ml-auto text-[10px] text-slate-400">
            Last run {new Date(report.generated_at).toLocaleString()} by {report.generated_by || "—"}
          </p>
        )}
      </div>

      {!report ? (
        <div data-testid="validation-empty" className="py-16 text-center text-xs text-slate-400 border border-dashed border-slate-200">
          No validation report yet — click <b>Run full validation</b> to sweep every module.
        </div>
      ) : (
        <>
          <div data-testid="validation-final-banner"
            className={`px-4 py-3 border flex items-center gap-3 ${report.final_approved_blocked
              ? "bg-red-50 border-red-200" : "bg-emerald-50 border-emerald-200"}`}>
            {report.final_approved_blocked
              ? <><ShieldWarning size={18} weight="fill" className="text-red-700" />
                <div>
                  <p className="text-sm font-semibold text-red-800">Final approved is blocked.</p>
                  <p className="text-[11px] text-red-700">{report.totals.critical_failures} critical failure(s) must be resolved before external release.</p>
                </div></>
              : <><ShieldCheck size={18} weight="fill" className="text-emerald-700" />
                <div>
                  <p className="text-sm font-semibold text-emerald-800">All critical checks passed.</p>
                  <p className="text-[11px] text-emerald-700">Workspace may proceed to Final approved (release gate must also be clean).</p>
                </div></>
            }
          </div>

          <div data-testid="validation-totals" className="grid grid-cols-4 bg-white border border-slate-200 divide-x divide-slate-100">
            {[
              ["Total checks", report.totals.checks, "slate"],
              ["Passed", report.totals.passed, "emerald"],
              ["Failed", report.totals.failed, "red"],
              ["Critical failures", report.totals.critical_failures, "red"],
            ].map(([l, v, tone]) => (
              <div key={l} className="px-4 py-3">
                <p className="overline-label text-slate-400">{l}</p>
                <p className={`font-heading font-extrabold text-2xl mt-1 ${tone === "red" && v > 0 ? "text-red-600" : tone === "emerald" ? "text-emerald-700" : ""}`}>{v}</p>
              </div>
            ))}
          </div>

          {report.failed.length > 0 && (
            <section data-testid="failed-list" className="bg-white border border-slate-200 p-4">
              <p className="overline-label text-red-700">Failed checks</p>
              <ul className="mt-2 space-y-2 text-[11px]">
                {report.failed.map((c) => (
                  <li key={c.id} data-testid={`failed-${c.id}`} className="border-l-2 border-red-300 pl-2">
                    <div className="flex items-center gap-2">
                      <WarningCircle size={11} className="text-red-600" weight="fill" />
                      <b>{c.label}</b>
                      <Chip tone={SEV_TONE[c.severity] || SEV_TONE.medium}>{c.severity}</Chip>
                      {c.tag && <Chip tone="bg-slate-100 text-slate-600 border-slate-200">{c.tag}</Chip>}
                    </div>
                    {c.remediation && <p className="text-slate-500 mt-0.5">→ {c.remediation}</p>}
                    {c.evidence && Object.keys(c.evidence).length > 0 && (
                      <p className="text-[10px] text-slate-400 font-mono-data mt-0.5">{JSON.stringify(c.evidence)}</p>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          )}

          <section className="bg-white border border-slate-200 p-4">
            <div className="flex items-center justify-between">
              <p className="overline-label text-emerald-700">Passed checks ({report.passed.length})</p>
              <button data-testid="toggle-passed" onClick={() => setShowAllPassed((v) => !v)}
                className="text-[10px] font-semibold text-slate-500 hover:text-slate-900">
                {showAllPassed ? "Hide list" : "Show list"}
              </button>
            </div>
            {showAllPassed && (
              <ul className="mt-2 space-y-1 text-[11px]">
                {report.passed.map((c) => (
                  <li key={c.id} data-testid={`passed-${c.id}`} className="border-l-2 border-emerald-300 pl-2 text-slate-600">
                    <CheckCircle size={10} className="text-emerald-700 inline mr-1" weight="fill" />
                    {c.label}
                  </li>
                ))}
              </ul>
            )}
          </section>
        </>
      )}
    </PageShell>
  );
}
